import React, { useState, useEffect } from 'react';
import { X, Download, ChevronLeft, ChevronRight, Book, FileText, Smartphone, Edit3 } from 'lucide-react';

const SimpleBookViewer = ({ book, onClose }) => {
  const [currentPage, setCurrentPage] = useState(0);

  if (!book || !book.content) return null;

  const chapters = Object.keys(book.content).sort((a, b) => parseInt(a) - parseInt(b));

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        prevPage();
      } else if (e.key === 'ArrowRight') {
        nextPage();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPage]);

  // Create pages array
  const createPages = () => {
    const pages = [
      // Cover page
      {
        type: 'cover',
        title: 'Cover',
        content: book.covers && book.covers.length > 0 ? (
          // Use generated cover
          <div className="h-full bg-black flex items-center justify-center rounded-lg overflow-hidden">
            <img
              src={`data:image/png;base64,${book.covers[0].image_base64}`}
              alt="Book Cover"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        ) : (
          // Default cover template
          <div className="h-full bg-gradient-to-br from-purple-600 to-indigo-800 flex flex-col items-center justify-center text-white p-8 rounded-lg">
            <Book className="h-20 w-20 text-purple-200 mb-6" />
            <h1 className="text-4xl font-bold text-center mb-4">{book.title}</h1>
            <p className="text-xl text-purple-200 mb-6">Genre: {book.genre}</p>
            <p className="text-lg text-purple-300">{book.chapters} Chapters</p>
            <div className="mt-8 text-center">
              <p className="text-sm text-purple-200">Generated by AI BookMoney</p>
              <p className="text-xs text-purple-300">
                Created: {new Date(book.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        )
      }
    ];

    // First, create all chapter pages to calculate page numbers
    const tempPages = [];
    chapters.forEach((chapterNum) => {
      const chapterData = book.content[chapterNum];
      // Handle both old format (string) and new format (object with title/text)
      const isNewFormat = typeof chapterData === 'object' && chapterData !== null && 'title' in chapterData && 'text' in chapterData;
      const chapterTitle = isNewFormat ? chapterData.title : `Chapter ${chapterNum}`;
      const content = isNewFormat ? chapterData.text : (typeof chapterData === 'string' ? chapterData : '');
      
      if (!content || typeof content !== 'string') {
        console.warn(`Chapter ${chapterNum} has invalid content, skipping`);
        return;
      }
      
      const words = content.split(' ').filter(word => word.trim().length > 0);
      const wordsPerPage = 200;
      
      if (words.length <= wordsPerPage) {
        tempPages.push({
          chapter: chapterNum,
          title: chapterTitle,
          pageCount: 1,
          startPage: 0 // Will be calculated
        });
      } else {
        const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
        let pageCount = 1;
        let currentWordCount = 0;
        
        sentences.forEach((sentence) => {
          const sentenceWords = sentence.trim().split(' ').filter(w => w.trim().length > 0);
          if (currentWordCount + sentenceWords.length > wordsPerPage && currentWordCount > 0) {
            pageCount++;
            currentWordCount = sentenceWords.length;
          } else {
            currentWordCount += sentenceWords.length;
          }
        });
        
        tempPages.push({
          chapter: chapterNum,
          title: chapterTitle,
          pageCount: pageCount,
          startPage: 0 // Will be calculated
        });
      }
    });

    // Calculate start pages
    let currentPageNum = 2; // Start after cover page (TOC will be page 2)
    tempPages.forEach((tempPage) => {
      tempPage.startPage = currentPageNum;
      currentPageNum += tempPage.pageCount;
    });

    // Now create the Table of Contents with correct page numbers
    pages.push({
      type: 'toc',
      title: 'Table of Contents',
      content: (
        <div className="h-full bg-white p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Table of Contents</h2>
          <div className="space-y-4">
            {tempPages.map((tempPage) => (
              <div key={tempPage.chapter} className="flex justify-between items-center border-b border-gray-200 pb-2">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500">Chapter {tempPage.chapter}</span>
                  <span className="text-lg text-gray-700 font-medium">{tempPage.title}</span>
                </div>
                <button
                  onClick={() => setCurrentPage(tempPage.startPage)}
                  className="text-purple-600 hover:text-purple-800 font-medium"
                >
                  Page {tempPage.startPage + 1}
                  {tempPage.pageCount > 1 && ` - ${tempPage.startPage + tempPage.pageCount}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      )
    });

    // Add chapter pages
    chapters.forEach((chapterNum) => {
      const chapterData = book.content[chapterNum];
      // Handle both old format (string) and new format (object with title/text)
      const isNewFormat = typeof chapterData === 'object' && chapterData !== null && 'title' in chapterData && 'text' in chapterData;
      const chapterTitle = isNewFormat ? chapterData.title : `Chapter ${chapterNum}`;
      const content = isNewFormat ? chapterData.text : (typeof chapterData === 'string' ? chapterData : '');
      
      if (!content || typeof content !== 'string') {
        console.warn(`Chapter ${chapterNum} has invalid content, skipping`);
        return;
      }
      
      const words = content.split(' ').filter(word => word.trim().length > 0);
      const wordsPerPage = 200; // More realistic page length (about 1 page in a real book)
      
      if (words.length <= wordsPerPage) {
        // Single page chapter
        pages.push({
          type: 'chapter',
          chapter: chapterNum,
          title: chapterTitle,
          content: (
            <div className="h-full bg-white p-8 rounded-lg overflow-hidden">
              <div className="mb-6">
                <div className="text-sm text-gray-500 mb-1">Chapter {chapterNum}</div>
                <h2 className="text-2xl font-bold text-gray-800">{chapterTitle}</h2>
              </div>
              <div className="text-gray-700 leading-relaxed text-justify prose prose-gray max-w-none">
                {content.split('\n\n').map((paragraph, idx) => (
                  paragraph.trim() && (
                    <p key={idx} className="mb-4">
                      {paragraph}
                    </p>
                  )
                ))}
              </div>
              <div className="absolute bottom-4 right-4 text-gray-400 text-sm">
                Page {pages.length + 1}
              </div>
            </div>
          )
        });
      } else {
        // Multi-page chapter - split by words but try to break at sentence endings
        const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
        let currentPageWords = [];
        let currentWordCount = 0;
        let pageNumber = 1;
        const chapterStartPageNum = pages.length + 1;
        
        sentences.forEach((sentence, sentenceIdx) => {
          const sentenceWords = sentence.trim().split(' ').filter(w => w.trim().length > 0);
          
          // If adding this sentence would exceed word limit and we already have content, start new page
          if (currentWordCount + sentenceWords.length > wordsPerPage && currentPageWords.length > 0) {
            // Create page with current content
            const pageContent = currentPageWords.join(' ').trim() + '.';
            
            pages.push({
              type: 'chapter',
              chapter: chapterNum,
              page: pageNumber,
              title: pageNumber === 1 ? chapterTitle : `${chapterTitle} (cont.)`,
              content: (
                <div className="h-full bg-white p-8 rounded-lg overflow-hidden">
                  {pageNumber === 1 && (
                    <div className="mb-6">
                      <div className="text-sm text-gray-500 mb-1">Chapter {chapterNum}</div>
                      <h2 className="text-2xl font-bold text-gray-800">{chapterTitle}</h2>
                    </div>
                  )}
                  {pageNumber > 1 && (
                    <div className="mb-4">
                      <div className="text-xs text-gray-400 mb-1">Chapter {chapterNum}</div>
                      <h3 className="text-lg font-semibold text-gray-600">{chapterTitle} (continued)</h3>
                    </div>
                  )}
                  <div className="text-gray-700 leading-relaxed text-justify prose prose-gray max-w-none">
                    {pageContent.split('\n\n').map((paragraph, idx) => (
                      paragraph.trim() && (
                        <p key={idx} className="mb-4">
                          {paragraph}
                        </p>
                      )
                    ))}
                  </div>
                  <div className="absolute bottom-4 right-4 text-gray-400 text-sm">
                    Page {chapterStartPageNum + pageNumber - 1}
                  </div>
                </div>
              )
            });
            
            // Reset for next page
            currentPageWords = [];
            currentWordCount = 0;
            pageNumber++;
          }
          
          // Add sentence to current page
          currentPageWords.push(...sentenceWords);
          currentWordCount += sentenceWords.length;
          
          // If this is the last sentence, create the final page
          if (sentenceIdx === sentences.length - 1 && currentPageWords.length > 0) {
            const pageContent = currentPageWords.join(' ').trim() + '.';
            
            pages.push({
              type: 'chapter',
              chapter: chapterNum,
              page: pageNumber,
              title: pageNumber === 1 ? chapterTitle : `${chapterTitle} (cont.)`,
              content: (
                <div className="h-full bg-white p-8 rounded-lg overflow-hidden">
                  {pageNumber === 1 && (
                    <div className="mb-6">
                      <div className="text-sm text-gray-500 mb-1">Chapter {chapterNum}</div>
                      <h2 className="text-2xl font-bold text-gray-800">{chapterTitle}</h2>
                    </div>
                  )}
                  {pageNumber > 1 && (
                    <div className="mb-4">
                      <div className="text-xs text-gray-400 mb-1">Chapter {chapterNum}</div>
                      <h3 className="text-lg font-semibold text-gray-600">{chapterTitle} (continued)</h3>
                    </div>
                  )}
                  <div className="text-gray-700 leading-relaxed text-justify prose prose-gray max-w-none">
                    {pageContent.split('\n\n').map((paragraph, idx) => (
                      paragraph.trim() && (
                        <p key={idx} className="mb-4">
                          {paragraph}
                        </p>
                      )
                    ))}
                  </div>
                  <div className="absolute bottom-4 right-4 text-gray-400 text-sm">
                    Page {chapterStartPageNum + pageNumber - 1}
                  </div>
                </div>
              )
            });
          }
        });
      }
    });

    return pages;
  };

  const pages = createPages();

  const nextPage = () => {
    setCurrentPage(prev => {
      const next = Math.min(prev + 1, pages.length - 1);
      return next;
    });
  };

  const prevPage = () => {
    setCurrentPage(prev => {
      const previous = Math.max(prev - 1, 0);
      return previous;
    });
  };

  const handleDownload = async (format = 'pdf') => {
    
    if (format === 'txt') {
      // Keep the original text download functionality
      const content = `${book.title}\n\nGenre: ${book.genre}\nChapters: ${book.chapters}\nCreated: ${new Date(book.created_at).toLocaleDateString()}\n\n${chapters.map(chapterNum => 
        `Chapter ${chapterNum}\n\n${book.content[chapterNum]}\n\n`
      ).join('')}`;
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${book.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    }
    
    // For PDF, EPUB, DOCX - use the backend API
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/books/${book.id}/download/${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        console.error(`${format.toUpperCase()} download failed with status:`, response.status);
        throw new Error(`Failed to generate ${format.toUpperCase()}`);
      }
      
      const data = await response.json();
      
      // Convert base64 to blob and download
      const byteCharacters = atob(data.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: data.content_type });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error(`Error downloading ${format.toUpperCase()}:`, error);
      alert(`Failed to download ${format.toUpperCase()}. Please try again.`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-2xl max-w-6xl w-full h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-white truncate mr-4">{book.title}</h2>
            <span className="text-sm text-gray-400">
              Page {currentPage + 1} of {pages.length}
            </span>
          </div>
          <div className="flex items-center space-x-4">
            {/* Download Dropdown */}
            <div className="relative group">
              <button className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors">
                <Download className="h-4 w-4" />
                <span>Download</span>
              </button>
              <div className="absolute right-0 top-full mt-2 bg-gray-800 border border-gray-600 rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[9999]">
                <div className="py-2 min-w-[120px]">
                  <button
                    onClick={() => handleDownload('pdf')}
                    className="flex items-center space-x-2 w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-sm"
                  >
                    <FileText className="h-4 w-4" />
                    <span>PDF (Print)</span>
                  </button>
                  <button
                    onClick={() => handleDownload('epub')}
                    className="flex items-center space-x-2 w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-sm"
                  >
                    <Smartphone className="h-4 w-4" />
                    <span>EPUB (Kindle)</span>
                  </button>
                  <button
                    onClick={() => handleDownload('docx')}
                    className="flex items-center space-x-2 w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-sm"
                  >
                    <Edit3 className="h-4 w-4" />
                    <span>DOCX (Edit)</span>
                  </button>
                  <button
                    onClick={() => handleDownload('txt')}
                    className="flex items-center space-x-2 w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-sm"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Text</span>
                  </button>
                </div>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Book Content */}
        <div className="flex-1 flex items-center justify-center p-6 relative overflow-hidden">
          {/* Navigation Buttons - Fixed positioning */}
          <div className="absolute inset-0 flex items-center justify-between pointer-events-none z-20">
            <button
              onClick={prevPage}
              disabled={currentPage === 0}
              className="ml-4 pointer-events-auto bg-black/60 text-white p-3 rounded-full hover:bg-black/80 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed shadow-lg"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            
            <button
              onClick={nextPage}
              disabled={currentPage === pages.length - 1}
              className="mr-4 pointer-events-auto bg-black/60 text-white p-3 rounded-full hover:bg-black/80 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed shadow-lg"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>

          {/* Page Content */}
          <div className="w-full max-w-4xl h-full relative z-10">
            <div className="h-full w-full rounded-lg shadow-2xl overflow-hidden">
              {pages[currentPage]?.content}
            </div>
          </div>
        </div>

        {/* Page Navigation */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center justify-between">
            {/* Left side - Navigation buttons */}
            <div className="flex items-center space-x-4">
              <button
                onClick={prevPage}
                disabled={currentPage === 0}
                className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </button>
              
              <button
                onClick={nextPage}
                disabled={currentPage === pages.length - 1}
                className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            
            {/* Center - Page title */}
            <div className="flex items-center space-x-4">
              <span className="text-gray-400 text-sm">
                {pages[currentPage]?.title || `Page ${currentPage + 1}`}
              </span>
            </div>
            
            {/* Page Dots */}
            <div className="flex items-center space-x-1">
              {pages.slice(0, Math.min(pages.length, 10)).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPage(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentPage ? 'bg-purple-400' : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                  title={`Go to page ${index + 1}`}
                />
              ))}
              {pages.length > 10 && (
                <span className="text-gray-500 text-xs ml-2">...</span>
              )}
            </div>
            
            {/* Right side - Page counter */}
            <div className="text-gray-400 text-sm">
              {currentPage + 1} / {pages.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleBookViewer;